/**
 * Iter 75 Phase 4 — Offline Queue Panel.
 *
 * Surfaces the localStorage-backed offline write queue to the field
 * user: pending, in-flight, dead-letter counts; manual retry / drop.
 * Always visible from the inspector status bar so a field user is
 * never surprised by lost work.
 *
 * GH-11 (real-plan certification, 2026-09-30) — "Drop" and "Drop
 * failed" used to persist an irreversible deletion on a single tap,
 * with no confirmation and no way back — real unsynced field work
 * (a measurement, a pin, a markup) could vanish from one accidental
 * touch. Every destructive action here now needs an explicit second
 * tap on a plain-language confirmation, AND still offers a genuine
 * Undo afterward (not just "are you sure" theater) for a real recovery
 * window — both reachable with touch alone, no keyboard shortcut.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Wifi, WifiOff, RefreshCw, Trash2, AlertTriangle, Inbox } from "lucide-react";
import { listQueue, retryDead, dropEntry, dropAllDead, restoreEntry, flush, enqueue as offlineEnqueue } from "@/lib/offlineQueueV2";
import { listQuarantinedLegacyPins, claimQuarantinedPin, discardQuarantinedPin } from "@/lib/offlineQueueV1Migration";

const UNDO_WINDOW_MS = 8000;
const CONFIRM_AUTO_CANCEL_MS = 5000;

export default function OfflineQueuePanel({ online = true }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [quarantined, setQuarantined] = useState([]);
  // GH-11 — which row (or "dead" for the bulk action) is currently
  // showing its "are you sure" confirmation, auto-cancelled if ignored.
  const [confirming, setConfirming] = useState(null);
  const confirmTimerRef = useRef(null);
  const refresh = () => {
    setEntries(listQueue());
    setQuarantined(listQuarantinedLegacyPins());
  };
  useEffect(() => {
    refresh();
    const h = () => refresh();
    window.addEventListener("forgedops:offline-queue", h);
    const tick = window.setInterval(refresh, 5000);
    return () => {
      window.removeEventListener("forgedops:offline-queue", h);
      window.clearInterval(tick);
      if (confirmTimerRef.current) window.clearTimeout(confirmTimerRef.current);
    };
  }, []);

  const armConfirm = (key) => {
    setConfirming(key);
    if (confirmTimerRef.current) window.clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = window.setTimeout(() => setConfirming(null), CONFIRM_AUTO_CANCEL_MS);
  };
  const cancelConfirm = () => {
    setConfirming(null);
    if (confirmTimerRef.current) window.clearTimeout(confirmTimerRef.current);
  };

  const confirmDropOne = (entry) => {
    cancelConfirm();
    const removed = dropEntry(entry.id);
    refresh();
    if (!removed) return;
    toast(`Discarded "${removed.op}" — this unsynced work will NOT be saved`, {
      duration: UNDO_WINDOW_MS,
      action: {
        label: "Undo",
        onClick: () => { restoreEntry(removed); refresh(); },
      },
    });
  };

  const confirmDropAllDead = () => {
    cancelConfirm();
    const removed = dropAllDead();
    refresh();
    if (!removed || removed.length === 0) return;
    toast(`Discarded ${removed.length} rejected item${removed.length === 1 ? "" : "s"} — this will NOT be saved`, {
      duration: UNDO_WINDOW_MS,
      action: {
        label: "Undo",
        onClick: () => { removed.forEach(restoreEntry); refresh(); },
      },
    });
  };

  const dead = entries.filter((e) => e.permanent === true);
  const pending = entries.length - dead.length;
  if (entries.length === 0 && quarantined.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60]" data-testid="offline-queue-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border ${
          dead.length > 0 || quarantined.length > 0
            ? "bg-[var(--fop-danger)] border-[var(--fop-danger)] text-black"
            : online
              ? "bg-[var(--fop-surface)] border-[var(--fop-border)] text-[var(--fop-text)]"
              : "bg-[var(--fop-warn)] border-[var(--fop-warn)] text-black"
        }`}
        data-testid="offline-queue-toggle"
        style={{ minHeight: 44 }}
        title={`Queue: ${pending} pending, ${dead.length} failed${quarantined.length ? `, ${quarantined.length} legacy item(s) need review` : ""}`}
      >
        {online ? <Wifi size={14} /> : <WifiOff size={14} />}
        <span className="text-[11px] font-mono uppercase tracking-wider">
          Queue · {pending}{dead.length ? ` · ${dead.length} failed` : ""}{quarantined.length ? ` · ${quarantined.length} legacy` : ""}
        </span>
        {(dead.length > 0 || quarantined.length > 0) && <AlertTriangle size={12} />}
      </button>
      {open && (
        <div className="absolute bottom-12 right-0 w-80 max-h-96 overflow-y-auto bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded shadow-2xl">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)]">
            <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Offline Queue</span>
            <div className="flex gap-1">
              <button onClick={() => { flush(); setTimeout(refresh, 400); }}
                      className="text-[11px] font-mono uppercase tracking-wider px-1.5 py-1 text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                      data-testid="offline-queue-flush"
                      title="Force flush"
                      style={{ minHeight: 44, minWidth: 44 }}>
                <RefreshCw size={12} />
              </button>
              {dead.length > 0 && (
                <>
                  <button onClick={() => { retryDead(); setTimeout(refresh, 400); }}
                          className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 text-[var(--fop-primary)]"
                          data-testid="offline-queue-retry-dead"
                          style={{ minHeight: 44 }}>
                    Retry failed
                  </button>
                  {confirming === "dead" ? (
                    <span className="flex items-center gap-1" data-testid="offline-queue-drop-dead-confirm">
                      <button onClick={confirmDropAllDead}
                              className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-[var(--fop-danger)] text-black"
                              data-testid="offline-queue-drop-dead-confirm-yes"
                              style={{ minHeight: 44 }}>
                        Discard {dead.length}
                      </button>
                      <button onClick={cancelConfirm}
                              className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 text-[var(--fop-text-3)]"
                              data-testid="offline-queue-drop-dead-confirm-no"
                              style={{ minHeight: 44 }}>
                        Keep
                      </button>
                    </span>
                  ) : (
                    <button onClick={() => armConfirm("dead")}
                            className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 text-[var(--fop-danger)]"
                            data-testid="offline-queue-drop-dead"
                            style={{ minHeight: 44 }}>
                      Drop failed
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          {entries.length === 0 ? (
            <div className="p-3 text-xs text-[var(--fop-text-3)]">Queue empty.</div>
          ) : (
            <ul>
              {entries.map((e) => {
                const isDead = e.permanent === true;
                const isConfirming = confirming === e.id;
                return (
                  <li key={e.id} className={`px-3 py-2 border-b border-[var(--fop-border)] flex items-start gap-2 ${isDead ? "bg-[rgba(239,68,68,0.06)]" : ""}`}
                      data-testid={`offline-queue-entry-${e.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[var(--fop-text)] font-medium truncate">{e.op}</div>
                      <div className="text-[10px] font-mono text-[var(--fop-text-3)] mt-0.5">
                        {isDead
                          ? `Rejected — needs your review${e.last_error ? ` · ${e.last_error}` : ""}`
                          : e.attempts > 0
                            ? `Retrying automatically (attempt ${e.attempts}) — will keep trying until it saves`
                            : "Waiting to sync"}
                      </div>
                    </div>
                    {isConfirming ? (
                      <div className="flex flex-col items-end gap-1 shrink-0" data-testid={`offline-queue-drop-${e.id}-confirm`}>
                        <span className="text-[9px] font-mono text-[var(--fop-text-3)] whitespace-nowrap">Discard for good?</span>
                        <span className="flex gap-1">
                          <button onClick={() => confirmDropOne(e)}
                                  className="text-[10px] font-mono uppercase px-2 py-1 rounded bg-[var(--fop-danger)] text-black"
                                  data-testid={`offline-queue-drop-${e.id}-confirm-yes`}
                                  style={{ minHeight: 44 }}>
                            Discard
                          </button>
                          <button onClick={cancelConfirm}
                                  className="text-[10px] font-mono uppercase px-2 py-1 text-[var(--fop-text-3)]"
                                  data-testid={`offline-queue-drop-${e.id}-confirm-no`}
                                  style={{ minHeight: 44 }}>
                            Keep
                          </button>
                        </span>
                      </div>
                    ) : (
                      <button onClick={() => armConfirm(e.id)}
                              className="text-[var(--fop-text-3)] hover:text-[var(--fop-danger)] p-2.5 shrink-0"
                              title="Discard this unsynced item (asks to confirm)"
                              data-testid={`offline-queue-drop-${e.id}`}
                              style={{ minHeight: 44, minWidth: 44 }}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {quarantined.length > 0 && (
            <div data-testid="offline-queue-quarantine-section">
              <div className="px-3 py-2 border-t border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)] flex items-center gap-1.5">
                <Inbox size={12} className="text-[var(--fop-text-3)]" />
                <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                  From an older app version — is this yours?
                </span>
              </div>
              <ul>
                {quarantined.map((q) => (
                  <li key={q.legacy_local_id} className="px-3 py-2 border-b border-[var(--fop-border)]"
                      data-testid={`offline-queue-quarantine-${q.legacy_local_id}`}>
                    <div className="text-xs text-[var(--fop-text)]">
                      An unsynced pin drop was found on this device from a previous
                      session ({new Date(q.queued_at).toLocaleString()}). It could not
                      be automatically attributed to a user — please confirm.
                    </div>
                    <div className="flex gap-2 mt-1.5">
                      <button
                        onClick={async () => { await claimQuarantinedPin(q.legacy_local_id, offlineEnqueue); refresh(); }}
                        className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-[var(--fop-cta-bg)] text-white"
                        data-testid={`offline-queue-quarantine-claim-${q.legacy_local_id}`}
                        style={{ minHeight: 44 }}
                      >
                        This is mine — sync it
                      </button>
                      <button
                        onClick={() => { discardQuarantinedPin(q.legacy_local_id); refresh(); }}
                        className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded border border-[var(--fop-border)] text-[var(--fop-text-3)]"
                        data-testid={`offline-queue-quarantine-discard-${q.legacy_local_id}`}
                        style={{ minHeight: 44 }}
                      >
                        Discard
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
